import React, { useEffect, useMemo, useState } from 'react';
import { FmlxModal, FmlxWizard } from 'fmlx-common-ui';
import OptimizationSelectLayer from './OptimizationSelectLayer';
import { GetScoreDefn, isNullOrUndefined, showAlertSnackBar } from 'utils/Global';
import {
  LayerTypeDTO,
  ListElementDTO,
  ListEnumDTO,
  ScoreDefinitionDTO,
} from 'business/models/Models';
import { CopyWellDetail } from 'business/models/CustomModels';
import { OptimizationContext } from './OptimizationContext';
import RMModal from 'components/modal/RMModal';
import { getIsDirty } from 'components/customComponents/Experiment/ImagingSchedule/ImagingScheduleData';
import { getISOConditionsList } from './IterativeScreenOptimizationHelper';
import { useRMSelector } from 'redux/hooks';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import { PropertyConstants } from 'enums/PropertyConstants';
import { selectIsLayerSelected } from 'redux/selectors/ExperimentStateSelector';
import useBroadcast, { CHANNEL_TYPES } from 'business/broadcast';
import { WellNavigationDetails } from 'components/Experiment/constants';
import { ConfigureOptimization } from './ConfigureOptimization';
import { CreateOptimization } from './CreateOptimization';
import { TreeService } from 'business/services/TreeService';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import ExperimentService from 'business/services/ExperimentService';
export const ISO_SCORE_GROUP_NAME = 'Default';
let selectedCopyWells: CopyWellDetail[] = [];

type OptimizationWizardProps = {
  setShow: (boolean) => void;
  selectedWells: WellNavigationDetails[];
};

export type OptimizationIngredientName = {
  name: string;
  stockNumber: number;
};
const experimentService = new ExperimentService();

const OptimizationWizard = ({ setShow, selectedWells }: OptimizationWizardProps) => {
  const [layerType, setLayerType] = useState<LayerTypeDTO>(LayerTypeDTO.Grid);
  const [substituteStock, setSubstituteStock] = useState<boolean>(false);
  const [isNewExperiment, setIsNewExperiment] = useState<boolean>(true);
  const [openExperiment, setOpenExperiment] = useState<boolean>(true);
  const [experimentId, setExperimentId] = useState<number>(312);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [experimentName, setExperimentName] = useState('');
  const [experimentUrl, setExperimentUrl] = useState('');
  const [isoScoresData, setIsoScoresData] = useState<ScoreDefinitionDTO[]>([]);
  const [cantOptimize, setCantOptimize] = useState<boolean>(false);
  const [cantOptimizeText, setCantOptimizeText] = useState<string>('');
  const [selectedWellsIngredientData, setSelectedWellsIngredientData] = useState<
    OptimizationIngredientName[]
  >([]);
  const [isoDisabled, setISODisabled] = useState<boolean>(false);
  const [isoPrecipitantIngs, setIsoPrecipitantIngs] = useState<
    OptimizationIngredientName[]
  >([]);
  const [plateTypeId, setPlateTypeId] = useState<number>(0);
  const exp = useRMSelector((state) => state.experiment.expData);
  const [sourceExperimentId, setSourceExperimentId] = useState<number>(exp?.experimentId);
  const isLayerSelected = useRMSelector(selectIsLayerSelected);
  const [validating, setValidating] = useState<boolean>(true);
  const [backDisabled, setBackDisabled] = useState<boolean>(true);
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const [activeStep, setActiveStep] = useState(0);
  const [openState, setOpenState] = useState(true);
  const [parentTreeNodeId, setParentTreeNodeId] = useState<number>();

  useEffect(() => {
    async function fetchDefaultPlateTypeId() {
      const response = await new GlobalPreferencesService().getProperty(
        PropertyConstants.Container,
      );
      const sourceExpContainerId = await experimentService.getExperimentContainerId(
        sourceExperimentId,
      );
      if (response != null) {
        const defaultPlateId = parseInt(
          ((response as ListEnumDTO).value as ListElementDTO).name,
        );
        defaultPlateId > 0
          ? setPlateTypeId(defaultPlateId)
          : setPlateTypeId(sourceExpContainerId);
      }
    }
    const fetchParentTreeNodeId = async () => {
      const treeService: TreeService = new TreeService();
      const id = await treeService.getParentIdByTreeNodeId(exp.treeNodeId);
      if (id) {
        setParentTreeNodeId(id);
      }
    };
    if (layerType !== LayerTypeDTO.Screen) {
      fetchParentTreeNodeId();
    }
    if (exp) {
      fetchDefaultPlateTypeId();
    }
    setSourceExperimentId(exp?.expId || exp?.experimentId);
  }, [exp]);

  useEffect(() => {
    if (isNullOrUndefined(selectedWells)) {
      return;
    }
    if (isNullOrUndefined(exp.plateDto[0].ingredients)) {
      return;
    }
    if (exp.isDirty || getIsDirty()) {
      setCantOptimizeText(
        'Optimized grids were not created because the source experiment contains changes that have not been saved.',
      );
      setCantOptimize(true);
      setValidating(false);
      return;
    }
    let isoDisabled = false;
    selectedCopyWells = [];
    selectedWells.forEach((wellDetail) => {
      const plate = exp.plateDto.find((p) => p.plateId == wellDetail.plateID);
      if (isNullOrUndefined(plate) || isNullOrUndefined(plate.ingredients)) {
        return;
      }
      const well = plate.ingredients.wellList.find(
        (w) => w.wellNumber == wellDetail.wellNum,
      );
      const x = (well.wellNumber % exp.containerCols) - 1;
      const y = parseInt((well.wellNumber / exp.containerCols).toString());
      selectedCopyWells.push({ id: well.wellId, wellLayers: well.wellLayerList, x, y });
      well.wellLayerList.forEach((wl) => {
        wl.wellLayerIngredientList.forEach((wli) => {
          selectedWellsIngredientData.push({
            name: wli.ingredientStockIngredient.longName,
            stockNumber: wli.ingredientStock.stockNumber,
          } as OptimizationIngredientName);
        });
      });
      isoDisabled = plate.plateNumber != 1;
    });
    if (isLayerSelected || selectedWells.length === 0) {
      setCantOptimizeText('No well(s) are selected for optimization.');
      setCantOptimize(true);
    } else if (selectedWellsIngredientData.length === 0) {
      setCantOptimizeText("Selected well(s) don't have any ingredients.");
      setCantOptimize(true);
    }
    setSelectedWellsIngredientData([...selectedWellsIngredientData]);
    if (isoScoresData.length == 0 && !isNullOrUndefined(GetScoreDefn())) {
      const scoreDefs: ScoreDefinitionDTO[] = GetScoreDefn();
      const wellDropScores = scoreDefs.filter(
        (sd) => sd.scoreGroupName == ISO_SCORE_GROUP_NAME,
      );
      setIsoScoresData([...wellDropScores]);
    }
    if (exp.screenLotId > 0) {
      isoDisabled = true;
    }
    if (!isoDisabled) {
      setIsoPrecipitantIngs(getISOConditionsList(exp));
    }
    setISODisabled(isoDisabled);
    setValidating(false);
  }, [selectedWells, exp]);

  enum WizardSteps {
    SelectOptimizationType = 0,
    DefineLocation = 1,
    CreateOptimization = 2,
  }

  const steps = [
    {
      id: WizardSteps.SelectOptimizationType,
      stepper: {
        title: 'SELECT Optimization TYPE',
        desc: 'Please SELECT Optimization TYPE',
        disabled: false,
        error: false,
      },
      content: <OptimizationSelectLayer />,
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        // code to move to the previous step
      },
    },
    {
      id: WizardSteps.DefineLocation,
      stepper: {
        title: 'Define Location',
        desc: 'Please Define Location',
        disabled: false,
        error: false,
      },
      content: <ConfigureOptimization totalNumOfWells={selectedWells.length} />,
      nextStepCallback: () => {
        if (
          isNewExperiment &&
          (isNullOrUndefined(experimentName) || experimentName.trim() === '')
        ) {
          showAlertSnackBar(
            EAlertType.ERROR,
            ESnackBarPosition.TOP,
            'Please enter experiment name',
          );
          return;
        }
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        setActiveStep((prevState) => prevState - 1);
      },
    },
    {
      id: WizardSteps.CreateOptimization,
      stepper: {
        title: 'Create Optimization',
        desc: 'Please create the Optimization',
        disabled: !nextEnabled,
        error: false,
      },
      content: <CreateOptimization wellDetails={[...selectedCopyWells]} />,
      nextStepCallback: () => {
        // code to move to the next step
      },
      backStepCallback: () => {
        setActiveStep((prevState) => prevState - 1);
      },
    },
  ];

  const handleCloseClick = () => {
    setShow(false);
    setOpenState(false);
  };

  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
  };

  const handleCancelClick = () => {
    setShow(false);
    setActiveStep(0);
  };

  const handleSkipClick = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevState) => prevState + 1);
    }
  };

  const nextButtonDisabled = useMemo(() => {
    switch (activeStep) {
      case WizardSteps.SelectOptimizationType:
        return false;
      case WizardSteps.DefineLocation:
        return !nextEnabled;
      case WizardSteps.CreateOptimization:
        return false;
    }
  }, [activeStep, nextEnabled]);

  const backButtonDisabled = useMemo(() => {
    switch (activeStep) {
      case WizardSteps.SelectOptimizationType:
        return false;
      case WizardSteps.DefineLocation:
        return false;
      case WizardSteps.CreateOptimization:
        return backDisabled;
    }
  }, [activeStep, nextEnabled]);

  return (
    <>
      {cantOptimize && (
        <RMModal
          id="optimization-wizard-set-show"
          setShowModal={setShow}
          showModal={true}
          okButtonText="OK"
          isOkOnly={true}
          onOk={() => setShow(false)}
          body={cantOptimizeText}
          heading={'Cannot Optimize'}
        />
      )}
      {!cantOptimize && !validating && (
        <OptimizationContext.Provider
          value={{
            layerType,
            setLayerType,
            substituteStock,
            setSubstituteStock,
            isNewExperiment,
            setIsNewExperiment,
            openExperiment,
            setOpenExperiment,
            experimentId,
            setExperimentId,
            sourceExperimentId,
            setSourceExperimentId,
            nextEnabled,
            setNextEnabled,
            setShow,
            experimentName,
            setExperimentName,
            experimentUrl,
            setExperimentUrl,
            isoScores: isoScoresData,
            setIsoScores: setIsoScoresData,
            selectedWellsIngredientData,
            isoPrecipitantIngs,
            isoDisabled,
            plateTypeId,
            setPlateTypeId,
            backDisabled,
            setBackDisabled,
            explorerBroadcastChannel,
            parentTreeNodeId,
          }}
        >
          <FmlxWizard
            id="optimization-wizard-action"
            title="Optimization Wizard"
            subtitle="Optimize Experiment"
            steps={steps}
            activeStep={activeStep}
            open={openState}
            onCloseClick={handleCloseClick}
            onStepClick={handleStepClick}
            onCancelClick={handleCancelClick}
            onSkipClick={handleSkipClick}
            onFinishClick={handleCloseClick}
            disableNextButton={nextButtonDisabled}
            disableBackButton={backButtonDisabled}
            type={FmlxWizard.Type.MODAL}
            modalSize={FmlxModal.Size.DOUBLE_EXTRA_LARGE}
          />
        </OptimizationContext.Provider>
      )}
    </>
  );
};

export default OptimizationWizard;
